import ApplicationController from '@aha-app/mvc';
import { type ObjectMenuSection } from './ObjectMenu';
import WhiteboardController from '../../controllers/WhiteboardController';
import type Stylus from '../../stylus/Stylus';
import { SpriteSvg } from './SpriteSvg';
import { type ShapeTabItem } from './ShapesTab';
import { type IconTabItem } from './IconsTab';
import {
  generateIconsMenuStructure,
  generateShapesMenuStructure,
} from '../../utils/objectMenu';
import SearchManager from './SearchManager';

export type ObjectMenuItem = IconTabItem | ShapeTabItem;

interface Tab {
  name: string;
  sections: ObjectMenuSection<ObjectMenuItem>[];
}
interface State {
  objectMenuStack: ObjectMenuSection<ObjectMenuItem>[][];
  tabs: Tab[];
  activeTabName: string;
  searchTerm: string;
  searchResults: ObjectMenuItem[];
}

class ObjectMenuController extends ApplicationController<State> {
  whiteboardController: WhiteboardController;
  searchManager: SearchManager;

  static initialState: State = {
    objectMenuStack: [],
    tabs: [],
    activeTabName: '',
    searchTerm: '',
    searchResults: [],
  };

  async initialize(): Promise<void> {
    this.whiteboardController =
      this.findControllerInstance(WhiteboardController)!;
    const shapesTab = {
      sections: generateShapesMenuStructure(this.whiteboardController),
      name: 'Shapes',
    };
    const hasFeatureOrShowPromo =
      window.currentAccount?.whiteboardsAdvanced ||
      window.currentAccount?.hasCrossProductPromotionsEnabled;

    const spriteIconSections = hasFeatureOrShowPromo
      ? await SpriteSvg.getInstance().getStructure()
      : [];

    const iconsTab = generateIconsMenuStructure(spriteIconSections);
    this.actionSetTabs([shapesTab, iconsTab]);

    this.searchManager = new SearchManager();
    this.rebuildSearchIndex();
  }

  get topLevel() {
    return this.state.objectMenuStack.length === 1;
  }

  get backButtonVisible() {
    return this.state.objectMenuStack.length > 1;
  }

  rebuildSearchIndex() {
    const sections = this.topLevel
      ? this.state.tabs.flatMap(tab => tab.sections)
      : this.state.objectMenuStack[this.state.objectMenuStack.length - 1];
    this.searchManager?.rebuildSearchIndex(sections);
  }

  actionSetTabs(tabs: Tab[]) {
    this.state.tabs = tabs;
    this.state.activeTabName = tabs[0].name;
    this.state.objectMenuStack.push(tabs[0].sections);
    this.actionSetActiveTab(tabs[0].name);
    this.rebuildSearchIndex();
  }

  actionSetActiveTab(tabName: string) {
    const tab = this.state.tabs.find(tab => tab.name === tabName);
    if (!tab) {
      return;
    }
    this.state.activeTabName = tabName;
    this.state.objectMenuStack = [tab.sections];
    this.state.searchTerm = '';
    this.rebuildSearchIndex();
  }

  activeSections<
    T extends ObjectMenuItem = ObjectMenuItem
  >(): ObjectMenuSection<T>[] {
    const index = this.state.objectMenuStack.length - 1;
    return (this.state.objectMenuStack[index] ?? []) as ObjectMenuSection<T>[];
  }

  actionPushSections(sections: ObjectMenuSection<ObjectMenuItem>[]) {
    this.state.objectMenuStack.push(sections);
    this.rebuildSearchIndex();
  }

  actionPopSections() {
    if (this.state.objectMenuStack.length > 1) {
      this.state.objectMenuStack.pop();
      this.rebuildSearchIndex();
    }
  }

  actionMenuBack() {
    this.actionPopSections();
  }

  actionCloseMenu(activateStylus?: Stylus) {
    if (activateStylus) {
      this.whiteboardController.proxiedThis.actionSetShapeStylus(
        activateStylus
      );
    }
    this.whiteboardController.proxiedThis.actionSetStylusMenu(null);
    this.actionResetMenu();
  }

  actionResetMenu() {
    if (!this.whiteboardController.state.objectMenuPinned) {
      this.actionSetActiveTab(this.state.tabs[0]?.name);
    }
  }

  actionClearSearchTerm() {
    this.state.searchTerm = '';
    this.actionSetActiveTab(this.state.tabs[0]?.name);
  }

  actionUpdateSearchResults(searchTerm: string) {
    this.state.searchTerm = searchTerm;
    this.state.searchResults = this.searchManager.search(searchTerm);
  }
}

export default ObjectMenuController;
